'use client';

import * as React from 'react';
import { motion, useMotionValue } from 'motion/react';
import { ArrowRight, Gift, X } from 'lucide-react';
import type { ExclusiveOffer } from '@workspace/global-utils';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { OfferCountdownTimer } from '@workspace/ui/components/upgrade-offer';
import { useOfferCountdown } from '@workspace/ui/hooks/use-offer-countdown';
import { OFFER_POSITION_STORAGE_KEY } from '@workspace/ui/lib/upgrade-offer';
import { cn } from '@workspace/ui/lib/utils';

const readSavedPosition = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(OFFER_POSITION_STORAGE_KEY) ?? 'null');
    return typeof saved?.x === 'number' && typeof saved?.y === 'number'
      ? (saved as { x: number; y: number })
      : { x: 0, y: 0 };
  } catch {
    return { x: 0, y: 0 };
  }
};

const savePosition = (position: { x: number; y: number }) => {
  try {
    window.localStorage.setItem(OFFER_POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Remembering the position is a convenience; it resets to the corner next time.
  }
};

const pad = (value: number) => String(value).padStart(2, '0');

// Draggable gift, so it can be moved off anything it covers. Gone once the offer ends.
export function OfferFloatingButton({
  offer,
  onClaim,
  dataPhSelector,
  className,
}: {
  offer: ExclusiveOffer;
  onClaim: () => void;
  dataPhSelector: string;
  className?: string;
}) {
  const countdown = useOfferCountdown(offer);
  const [open, setOpen] = React.useState(false);
  const boundsRef = React.useRef<HTMLDivElement>(null);
  const dragged = React.useRef(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  React.useEffect(() => {
    const saved = readSavedPosition();
    x.set(saved.x);
    y.set(saved.y);
  }, [x, y]);

  if (countdown.phase === 'ended') return null;

  const urgent = countdown.phase === 'last_call';
  const { days, hours, minutes, seconds } = countdown;
  const shortTime =
    days > 0
      ? `${days}d ${hours}h`
      : hours > 0
        ? `${hours}h ${pad(minutes)}m`
        : `${pad(minutes)}:${pad(seconds)}`;

  return (
    <div ref={boundsRef} className="pointer-events-none fixed inset-0 z-28">
      <motion.div
        drag
        dragConstraints={boundsRef}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        onDragStart={() => {
          dragged.current = true;
          setOpen(false);
        }}
        onDragEnd={() => {
          savePosition({ x: x.get(), y: y.get() });
          // The click that ends a drag fires after this, so clear the flag afterwards.
          window.setTimeout(() => {
            dragged.current = false;
          });
        }}
        className={cn(
          'pointer-events-auto absolute right-5 top-1/2 flex -translate-y-1/2 touch-none flex-col items-center gap-1',
          className,
        )}
      >
        <Popover
          open={open}
          onOpenChange={(next) => {
            if (!dragged.current) setOpen(next);
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`${offer.discountPercent}% off offer`}
              data-ph-selector={`${dataPhSelector}-open`}
              className={cn(
                'relative grid size-11 cursor-grab place-items-center rounded-full ring-2 active:cursor-grabbing',
                urgent
                  ? 'bg-offer-urgent shadow-offer-urgent ring-offer-amber-2'
                  : 'bg-offer shadow-offer ring-offer-gold-2',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none absolute -inset-[5px] rounded-full border-2',
                  urgent
                    ? 'animate-offer-ping-fast border-offer-amber-2'
                    : 'animate-offer-ping border-offer-gold-2',
                )}
                aria-hidden="true"
              />
              <Gift className="size-5 text-white" aria-hidden="true" />
              <span
                className={cn(
                  'absolute -top-2 rounded-full px-1.5 py-px text-[10px] font-bold text-offer-ink',
                  urgent
                    ? 'bg-offer-amber left-1/2 -translate-x-1/2 whitespace-nowrap'
                    : 'bg-offer-gold -right-2',
                )}
              >
                {urgent ? 'LAST CALL' : `${offer.discountPercent}%`}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="end"
            sideOffset={14}
            collisionPadding={12}
            className={cn(
              'offer-shine flex w-[300px] flex-col gap-3 rounded-2xl border-0 p-4 text-white',
              urgent ? 'bg-offer-urgent shadow-offer-urgent' : 'bg-offer shadow-offer',
            )}
          >
            <div className="flex items-center gap-2.5">
              <span
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-lg text-offer-ink',
                  urgent ? 'bg-offer-amber' : 'bg-offer-gold',
                )}
              >
                <Gift className="size-4" aria-hidden="true" />
              </span>
              <p className="flex-1 text-[15px] font-bold">
                {offer.discountPercent}% off every plan
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-ph-selector={`${dataPhSelector}-close`}
                className="cursor-pointer text-white/70 transition-[color] duration-150 hover:text-white"
              >
                <X className="size-4" aria-hidden="true" />
                <span className="sr-only">Close</span>
              </button>
            </div>
            <p className="text-[13px] text-white/75">
              {urgent
                ? `Last call. ${offer.discountPercent}% off ends in a few hours.`
                : `Your ${offer.discountPercent}% off is waiting. Ends soon.`}
            </p>
            <OfferCountdownTimer countdown={countdown} className="gap-2 text-[26px]" />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onClaim();
              }}
              data-ph-selector={`${dataPhSelector}-claim`}
              className={cn(
                'inline-flex h-10 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] text-sm font-bold text-offer-ink transition-[filter] duration-150 hover:brightness-105',
                urgent ? 'bg-offer-amber' : 'bg-offer-gold',
              )}
            >
              Claim {offer.discountPercent}% off
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          </PopoverContent>
        </Popover>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 font-mono text-[11px] font-medium text-white tabular-nums',
            urgent ? 'bg-offer-urgent' : 'bg-offer',
          )}
        >
          {shortTime}
        </span>
      </motion.div>
    </div>
  );
}
